import { Button } from '@/components/ui/Button'
import { ArrowRight, BarChart3, Users, Zap, CheckCircle, ArrowUpRight } from 'lucide-react'
import { useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import Cookies from 'js-cookie'

const API_URL = import.meta.env.VITE_API_BASE_URL || '';
const VISIT_COOKIE_NAME = 'validea_visit_recorded';
const VISIT_COOKIE_EXPIRY = 7; // días

interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  [key: string]: string | undefined;
}

const getUtmParams = (): UtmParams => {
  if (typeof window === 'undefined') return {};
  
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: UtmParams = {};
  
  ['utm_source', 'utm_medium', 'utm_campaign'].forEach(param => {
    const value = urlParams.get(param);
    if (value) utmParams[param] = value;
  });
  
  return utmParams;
};

const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browserName = "Unknown";
  let os = "Unknown";

  // Detectar navegador
  if (ua.indexOf("Chrome") > -1) browserName = "Chrome";
  else if (ua.indexOf("Safari") > -1) browserName = "Safari";
  else if (ua.indexOf("Firefox") > -1) browserName = "Firefox";
  else if (ua.indexOf("Edge") > -1) browserName = "Edge";
  else if (ua.indexOf("Opera") > -1 || ua.indexOf("OPR") > -1) browserName = "Opera";

  // Detectar SO
  if (ua.indexOf("Win") > -1) os = "Windows";
  else if (ua.indexOf("Mac") > -1) os = "MacOS";
  else if (ua.indexOf("Linux") > -1) os = "Linux";
  else if (ua.indexOf("Android") > -1) os = "Android";
  else if (ua.indexOf("iOS") > -1) os = "iOS";

  return { browserName, os };
};

const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return "mobile";
  }
  return "desktop";
};

const quickBenefits = [
  {
    icon: BarChart3,
    text: 'Analítica en tiempo real',
    description: 'Monitorea el comportamiento de tus usuarios'
  },
  {
    icon: Users,
    text: 'Formularios inteligentes',
    description: 'Captura feedback valioso automáticamente'
  },
  {
    icon: Zap,
    text: 'Priorización automática',
    description: 'Toma decisiones basadas en datos reales'
  },
]

const stats = [
  { value: '93%', label: 'Tasa de respuesta' },
  { value: '24/7', label: 'Monitoreo continuo' },
  { value: '+1000', label: 'MVPs validados' },
]

/**
 * Hero section component for the landing page
 * Displays the main value proposition and call-to-action
 */
export function Hero() {
  useEffect(() => {
    const recordVisit = async () => {
      // Skip recording if it's Vercel's screenshot service
      if (navigator.userAgent.includes('vercel-screenshot/1.0')) return;

      // Verificar si ya se registró la visita
      if (Cookies.get(VISIT_COOKIE_NAME)) return;

      const { browserName, os } = getBrowserInfo();
      const deviceType = getDeviceType();
      const utmParams = getUtmParams();

      const visitData = {
        mvpId: "alineatec-validea-mvp",
        uuid: uuidv4(),
        timestamp: new Date().toISOString(),
        referrer: document.referrer || window.location.origin,
        userAgent: navigator.userAgent,
        language: navigator.language,
        device: {
          type: deviceType,
          os: os,
          browser: browserName
        },
        screen: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        location: {
          country: "Desconocido", // Se podría usar un servicio de geolocalización
          region: "Desconocido",
          city: "Desconocido"
        },
        utmParams
      };

      try {
        const response = await fetch(`${API_URL}/visits`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(visitData)
        });

        if (response.ok) {
          // Guardar cookie para no registrar la visita nuevamente
          Cookies.set(VISIT_COOKIE_NAME, '1', { expires: VISIT_COOKIE_EXPIRY });
        }
      } catch (error) {
        console.error('Error registrando la visita:', error);
      }
    };

    recordVisit();
  }, []);

  return (
    <section id="hero" className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50 py-20 sm:py-32">
      {/* Background elements */}
      <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />
      <div className="absolute -top-4 -left-4 h-72 w-72 animate-blob rounded-full bg-primary-300/30 mix-blend-multiply blur-xl filter" />
      <div className="absolute -bottom-8 right-0 h-72 w-72 animate-blob animation-delay-2000 rounded-full bg-secondary-300/30 mix-blend-multiply blur-xl filter" />
      <div className="absolute -bottom-8 left-20 h-72 w-72 animate-blob animation-delay-4000 rounded-full bg-primary-200/30 mix-blend-multiply blur-xl filter" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Top badge */}
        <div className="mx-auto max-w-2xl text-center mb-10">
          <div className="inline-flex items-center rounded-full px-3 py-1 text-sm leading-6 text-primary-600 ring-1 ring-primary-200 hover:ring-primary-300 transition-all duration-150 group">
            <span className="font-semibold">Nuevo lanzamiento</span>
            <ArrowUpRight className="ml-1 h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-8">
          {/* Text content */}
          <div className="flex flex-col justify-center max-w-xl mx-auto lg:mx-0">
            <h1 className="text-5xl font-bold tracking-tight text-gray-900 sm:text-6xl lg:text-7xl">
              <span className="block">Validá tus ideas</span>
              <span className="block text-primary-600 mt-2">antes de construirlas</span>
            </h1>
            <p className="mt-8 text-xl text-gray-600 leading-relaxed">
              Con Validea, descubrí qué productos realmente interesan a tus usuarios. Obtené insights valiosos a través de analítica avanzada, formularios inteligentes y priorización basada en datos reales.
            </p>
            
            {/* Stats */}
            <div className="mt-12 grid grid-cols-3 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
                  <div className="mt-1 text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Quick benefits */}
            <div className="mt-12 space-y-4">
              {quickBenefits.map((benefit) => (
                <div key={benefit.text} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                  <div className="flex-shrink-0 bg-primary-50 rounded-lg p-2">
                    <benefit.icon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{benefit.text}</h3>
                    <p className="mt-1 text-sm text-gray-500">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <a
                href="#contact"
                className="inline-flex items-center justify-center bg-primary-600 hover:bg-primary-500 text-white font-semibold px-8 py-4 rounded-full shadow-lg text-lg transition-all duration-200 hover:scale-105 hover:shadow-xl"
              >
                Solicitar acceso beta
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <a
                href="#howitworks"
                className="inline-flex items-center justify-center bg-white text-primary-600 font-semibold px-8 py-4 rounded-full shadow-lg text-lg transition-all duration-200 hover:scale-105 hover:shadow-xl border border-primary-100 hover:bg-primary-50"
              >
                Ver cómo funciona
                <CheckCircle className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Dashboard mockup */}
          <div className="relative lg:mt-0 mt-12">
            <div className="relative mx-auto w-full max-w-lg">
              <div className="relative overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200 transition-all duration-300 hover:shadow-3xl">
                {/* Mockup header */}
                <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50/80 backdrop-blur-sm px-6 py-4">
                  <div className="flex gap-1.5">
                    <div className="h-3 w-3 rounded-full bg-red-500" />
                    <div className="h-3 w-3 rounded-full bg-yellow-500" />
                    <div className="h-3 w-3 rounded-full bg-green-500" />
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="h-4 w-24 rounded-full bg-gray-200" />
                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <Users className="h-4 w-4 text-primary-600" />
                    </div>
                  </div>
                </div>
                
                {/* Mockup content */}
                <div className="p-6">
                  <div className="space-y-6">
                    {/* Chart */}
                    <div className="h-64 rounded-xl bg-gradient-to-br from-gray-50 to-white p-6 ring-1 ring-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div className="space-y-1">
                          <div className="h-4 w-32 rounded-full bg-gray-200" />
                          <div className="h-3 w-24 rounded-full bg-gray-100" />
                        </div>
                        <div className="flex gap-2">
                          <div className="h-8 w-8 rounded-lg bg-gray-100" />
                          <div className="h-8 w-8 rounded-lg bg-gray-100" />
                        </div>
                      </div>
                      <div className="h-40 w-full rounded-lg bg-gradient-to-br from-primary-100 to-primary-50 relative">
                        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-primary-500/10 to-transparent rounded-b-lg" />
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="rounded-xl bg-gradient-to-br from-gray-50 to-white p-4 ring-1 ring-gray-100">
                          <div className="h-4 w-16 rounded-full bg-gray-200" />
                          <div className="mt-2 h-6 w-24 rounded-full bg-gray-200" />
                        </div>
                      ))}
                    </div>
                    
                    {/* Recent activity */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="h-4 w-32 rounded-full bg-gray-200" />
                        <div className="h-8 w-8 rounded-lg bg-gray-100" />
                      </div>
                      <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-br from-gray-50 to-white ring-1 ring-gray-100">
                            <div className="h-10 w-10 rounded-full bg-gray-200" />
                            <div className="space-y-2 flex-1">
                              <div className="h-4 w-full rounded-full bg-gray-200" />
                              <div className="h-3 w-24 rounded-full bg-gray-100" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 h-72 w-72 animate-blob rounded-full bg-primary-100 opacity-50 mix-blend-multiply blur-3xl filter" />
              <div className="absolute -bottom-8 -left-4 h-72 w-72 animate-blob animation-delay-4000 rounded-full bg-secondary-100 opacity-50 mix-blend-multiply blur-3xl filter" />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
} 